import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';
import Button from './Button';
import BackendStatus from './BackendStatus';
import ChangePasswordModal from '../../pages/settings/components/ChangePasswordModal';

const HeaderNavigation = ({ onSidebarToggle, notifications = [], previewMode = false, showSidebarToggle = true }) => {
  const { user, userProfile, signOut } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const notificationRef = useRef(null);
  const userMenuRef = useRef(null);

  const unreadCount = notifications?.filter((n) => !n?.read)?.length || 0;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      setShowUserMenu(false);
    } catch (error) {
      console.log('Sign out error:', error);
    }
  };

  const currentUser = previewMode ? null : userProfile || user;

  const getNotificationColor = (type) => {
    const colors = {
      info: 'bg-blue-500',
      success: 'bg-green-500',
      warning: 'bg-yellow-500',
      error: 'bg-red-500'
    };
    return colors[type] || 'bg-gray-500';
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border shadow-sm">
      <div className="flex items-center justify-between h-16 px-4 sm:px-6">
        {/* Left Section - Mobile responsive */}
        <div className="flex items-center gap-3 sm:gap-4">
          {/* Mobile Menu Button */}
          {showSidebarToggle && onSidebarToggle && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onSidebarToggle}
              className="lg:hidden"
              title="Toggle Menu"
            >
              <Icon name="Menu" size={20} />
            </Button>
          )}

          {/* Logo and Brand - Responsive */}
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="Users" size={16} color="white" className="sm:w-5 sm:h-5" />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-lg font-bold text-foreground">TechStaff Pro</h1>
              <p className="text-xs text-muted-foreground">Placement Management</p>
            </div>
            {/* Mobile brand - simplified */}
            <div className="block sm:hidden">
              <h1 className="text-base font-bold text-foreground">TechStaff</h1>
            </div>
          </div>
        </div>

        {/* Right Section - Mobile responsive */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Backend Status Indicator - Development Mode */}
          {import.meta.env.DEV && (
            <BackendStatus />
          )}

          {/* Website Link */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => { window.location.href = '/'; }}
            title="Go to Website"
            className="text-muted-foreground hover:text-primary transition-colors"
          >
            <Icon name="Globe" size={18} />
          </Button>

          {/* Keyboard Shortcut Indicator - Desktop only */}
          <div className="hidden lg:flex items-center gap-2 text-xs text-muted-foreground">
            <kbd className="px-2 py-1 bg-muted rounded text-xs">Ctrl+K</kbd>
            <span>Search</span>
          </div>

          {/* Preview Mode Sign In Button */}
          {previewMode && (
            <div className="flex items-center gap-2">
              <Button
                onClick={() => window.location.href = '/erp/login-authentication'}
                className="text-xs sm:text-sm px-3 py-1 sm:px-4 sm:py-2"
              >
                <Icon name="LogIn" size={14} className="mr-1 sm:mr-2" />
                Sign In
              </Button>
            </div>
          )}

          {/* Authenticated User Controls */}
          {!previewMode && currentUser && (
            <>
              {/* Notifications - Hidden on small mobile */}
              <div className="relative hidden xs:block" ref={notificationRef}>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative"
                  title="Notifications"
                >
                  <Icon name="Bell" size={18} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </Button>

                {/* Notifications Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-card border border-border rounded-lg shadow-lg z-50">
                    <div className="p-3 sm:p-4 border-b border-border">
                      <h3 className="font-medium text-foreground text-sm sm:text-base">Notifications</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications?.length > 0 ? (
                        notifications.map((notification) => (
                          <div
                            key={notification.id}
                            className={`p-3 sm:p-4 border-b border-border hover:bg-muted/50 cursor-pointer ${!notification.read ? 'bg-muted/20' : ''
                              }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-2 h-2 rounded-full mt-2 ${getNotificationColor(notification.type)}`} />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">{notification.title}</p>
                                <p className="text-xs text-muted-foreground mt-1">{notification.message}</p>
                                <p className="text-xs text-muted-foreground mt-1">{formatTime(notification.timestamp)}</p>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-8 text-center text-muted-foreground text-sm">
                          <Icon name="Bell" size={32} className="mx-auto mb-2 opacity-50" />
                          <p>No notifications</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* User Menu */}
              <div className="relative" ref={userMenuRef}>
                <Button
                  variant="ghost"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 px-2 sm:px-3 hover:bg-muted"
                  title="User Menu"
                >
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-xs sm:text-sm font-medium text-white">
                      {currentUser?.full_name?.charAt(0) || currentUser?.email?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <div className="text-left hidden sm:block">
                    <p className="text-sm font-medium text-foreground truncate max-w-32">
                      {currentUser?.full_name || currentUser?.email || 'User'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {currentUser?.role || 'Employee'}
                    </p>
                  </div>
                  <Icon name="ChevronDown" size={14} className="hidden sm:block" />
                </Button>

                {/* User Dropdown */}
                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-2 w-56 sm:w-64 bg-card border border-border rounded-lg shadow-lg z-50">
                    <div className="p-3 sm:p-4 border-b border-border">
                      <p className="font-medium text-foreground text-sm">{currentUser?.full_name || currentUser?.email || 'User'}</p>
                      <p className="text-xs text-muted-foreground">{currentUser?.email}</p>
                      <p className="text-xs text-primary mt-1 capitalize">{currentUser?.role || 'Employee'}</p>
                    </div>
                    <div className="p-2">
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setShowChangePassword(true);
                          setShowUserMenu(false);
                        }}
                        className="w-full justify-start text-sm px-3 py-2 mb-1"
                      >
                        <Icon name="Lock" size={14} className="mr-2" />
                        Change Password
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={handleSignOut}
                        className="w-full justify-start text-sm px-3 py-2 text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Icon name="LogOut" size={14} className="mr-2" />
                        Sign Out
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Change Password Modal */}
              <ChangePasswordModal 
                isOpen={showChangePassword} 
                onClose={() => setShowChangePassword(false)} 
              />
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default HeaderNavigation;